import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  MANUAL_INQUIRY_MAX_RECORDS,
  MANUAL_INQUIRY_MESSAGE_MAX,
  createManualInquiry,
  describeManualInquiryError,
  readManualInquiryResult,
} from '../../services/manualInquiryService';
import { isPlausibleEmail, isPlausiblePhone } from '../leadsConfig';
import LeadChannelPicker from './LeadChannelPicker';
import InfoNotice from '../../Components/InfoNotice';
import LeadPicker from './LeadPicker';
import LeadRecordPicker from './LeadRecordPicker';
import { sanitizePhoneInput } from '../../Utils/brandForm';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../hooks/useScrollEdgeFade';
import {
  appModalActionsSx,
  appModalCloseButtonSx,
  appModalHeaderDividerSx,
  appModalPaperSx,
  appModalTitleBarSx,
  appModalTitleSx,
} from '../../Components/appModalChrome.styles';
import {
  LEADS_FORM_FIELD_GAP,
  LEADS_FORM_LABEL_GAP,
  LEADS_FORM_SECTION_GAP,
  getLeadStageFilterChipSx,
  leadsAlertSx,
  leadsDateFieldSx,
  leadsGhostButtonSx,
  leadsGridChipSx,
  leadsPrimaryButtonSx,
  leadsSectionTitleSx,
  leadsTextFieldSx,
} from './leadsUi.styles';

// Alto fijo del diálogo (único valor a tocar). 700 px = alto estándar de los
// modales Terrio (`appModalPaperSx`, sm): «Lead existente» entra sin scroll
// (~655 px); «Lead nuevo» (~705 px) scroll mínimo con fade.
const MANUAL_INQUIRY_DIALOG_HEIGHT = 'min(90vh, 700px)';

// Espaciado único del formulario (tokens compartidos con «Nuevo lead» y
// «Editar datos»): entre secciones (Lead, Canal, mensaje, fecha, registros),
// del título de sección a su control y entre campos de una misma sección.
// Sin helper reservado (' '): un error aparece debajo de su campo.
const SECTION_GAP = LEADS_FORM_SECTION_GAP;
const LABEL_GAP = LEADS_FORM_LABEL_GAP;
const FIELD_GAP = LEADS_FORM_FIELD_GAP;

/** «Lead existente» / «Lead nuevo»: 2 columnas iguales a todo el ancho; a 12 px del campo. */
const leadModeGridSx = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.75, mb: FIELD_GAP };

const pad2 = (n) => String(n).padStart(2, '0');

/** `YYYY-MM-DDTHH:mm` en hora local (valor de `<input type="datetime-local">`). */
export function toDateTimeLocalValue(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (!Number.isFinite(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// La API tolera 5 min de reloj; acá 1 min para avisar antes de mandar.
const FUTURE_TOLERANCE_MS = 60 * 1000;

const emptyForm = () => ({
  mode: 'existing',
  lead: null,
  name: '',
  phone: '',
  email: '',
  channel: '',
  message: '',
  occurredAt: toDateTimeLocalValue(new Date()),
  records: [],
});

/**
 * Nueva consulta (POST /createmanualinquiry): una consulta que llegó por otro
 * canal (teléfono, WhatsApp, presencial…). Mismo chrome que `LeadCreateDialog`.
 *
 * - Lead existente (buscador) o nuevo (nombre + teléfono o email). Con
 *   `presetLead` (desde la ficha) el lead viene fijo.
 * - Canal (opcional), mensaje (obligatorio), fecha y hora (ahora, sin futuro)
 *   y hasta 20 registros (opcional; sin registro = consulta abierta).
 * - 409 LEAD_DUPLICATE → «Usar el lead existente» reintenta con `lead_id`.
 *
 * @param {{ open: boolean, ownerUserId?: string|null, presetLead?: object|null,
 *   onClose: () => void, onCreated: (result: object) => void }} props
 */
export default function LeadManualInquiryDialog({
  open,
  ownerUserId = null,
  presetLead = null,
  onClose,
  onCreated,
}) {
  const [form, setForm] = useState(emptyForm);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  // Contenido con scroll (alto fijo): fade arriba / abajo si hay más.
  const { scrollRef, fadeTop, fadeBottom } = useScrollEdgeFade();
  const presetRef = useRef(presetLead);
  presetRef.current = presetLead;

  // Se resetea al abrir (no si el lead fijo se re-renderiza con el diálogo abierto).
  useEffect(() => {
    if (!open) return;
    setForm({ ...emptyForm(), lead: presetRef.current || null });
    setTouched(false);
    setError(null);
  }, [open]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const update = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    if (error) setError(null);
  };
  const setField = (key) => (e) => update({ [key]: e.target.value });

  const isNew = !presetLead && form.mode === 'new';
  const nameMissing = isNew && !form.name.trim();
  const contactMissing = isNew && !form.email.trim() && !form.phone.trim();
  const emailInvalid = isNew && Boolean(form.email.trim()) && !isPlausibleEmail(form.email);
  const phoneInvalid = isNew && Boolean(form.phone.trim()) && !isPlausiblePhone(form.phone);
  const leadMissing = !isNew && !form.lead?.id;
  const messageMissing = !form.message.trim();
  const occurredMs = form.occurredAt ? new Date(form.occurredAt).getTime() : NaN;
  const occurredFuture = Number.isFinite(occurredMs) && occurredMs > Date.now() + FUTURE_TOLERANCE_MS;
  const invalid = nameMissing || contactMissing || emailInvalid || phoneInvalid || leadMissing
    || messageMissing || occurredFuture;

  const fieldError = (field) => (error?.field === field ? error.message : '');

  const submit = async (override = null) => {
    setTouched(true);
    const useLead = override || (!isNew ? form.lead : null);
    if (saving || (!override && invalid)) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setSaving(true);
    setError(null);
    try {
      const res = await createManualInquiry(
        {
          ...(useLead ? { leadId: useLead.id } : { lead: { name: form.name, email: form.email, phone: form.phone } }),
          channel: form.channel,
          message: form.message,
          occurredAt: form.occurredAt ? new Date(form.occurredAt) : null,
          records: form.records,
        },
        { ownerUserId, signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      if (res?._httpOk) {
        onCreated(readManualInquiryResult(res));
        return;
      }
      setError(describeManualInquiryError(res));
    } catch (err) {
      if (err?.name !== 'AbortError') setError({ message: 'No se pudo cargar la consulta.', field: '' });
    } finally {
      if (abortRef.current === controller) setSaving(false);
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    submit();
  };

  // 409: pasar a «lead existente» con ese id y reintentar sin tocar el resto.
  const useExistingLead = () => {
    const existing = {
      id: error.duplicateId,
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
    };
    setForm((prev) => ({ ...prev, mode: 'existing', lead: existing }));
    submit(existing);
  };

  const phoneHelper = fieldError('phone') || (touched && phoneInvalid
    ? 'Ingresá un teléfono válido (8–15 dígitos; +, espacios o guiones).'
    : touched && contactMissing ? 'Cargá al menos un teléfono o un email.' : fieldError('contact') || '');
  const emailHelper = fieldError('email') || (touched && emailInvalid
    ? 'Ingresá un email válido.'
    : touched && contactMissing ? 'Cargá al menos un teléfono o un email.' : fieldError('contact') || '');
  // Campos con lugar propio; el resto (canal, permisos, tope, 503, red…) va en la alerta.
  // Con el lead fijo (ficha) un error de lead también va en la alerta.
  const inlineFields = ['name', 'phone', 'email', 'contact', 'message', 'occurredAt', 'record', ...(presetLead ? [] : ['lead'])];
  const generalError = error && !(isNew ? inlineFields : inlineFields.filter((f) => !['name', 'phone', 'email', 'contact'].includes(f))).includes(error.field)
    ? error : null;
  // Duplicado en «Lead nuevo»: aviso bajo Teléfono / Email (donde se corrige).
  const duplicateInSection = Boolean(generalError?.duplicateId) && isNew;
  // Al aparecer, el aviso o la alerta se trae a la vista dentro del contenido.
  const feedbackRef = useRef(null);
  useEffect(() => {
    if (!generalError) return;
    const reduceMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    feedbackRef.current?.scrollIntoView?.({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [generalError]);

  const duplicateNotice = generalError?.duplicateId ? (
    <Box ref={feedbackRef}>
      <InfoNotice
        role="alert"
        data-testid="lead-duplicate-notice"
        title={generalError.message}
        actionLabel="Usar el lead existente"
        onAction={useExistingLead}
        actionDisabled={saving}
      />
    </Box>
  ) : null;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
        noValidate: true,
        sx: {
          ...appModalPaperSx,
          width: 'min(520px, 95vw)',
          maxWidth: '520px',
          // Alto fijo en todo estado (no crece); lo que no entra (modo nuevo,
          // resultados, avisos, errores) scrollea en DialogContent con fade y
          // el pie queda fijo.
          height: MANUAL_INQUIRY_DIALOG_HEIGHT,
          maxHeight: MANUAL_INQUIRY_DIALOG_HEIGHT,
        },
      }}
      aria-labelledby="lead-manual-inquiry-title"
    >
      <DialogTitle id="lead-manual-inquiry-title" sx={appModalTitleBarSx}>
        <Typography component="h2" sx={appModalTitleSx}>Nueva consulta</Typography>
        <IconButton size="small" onClick={onClose} disabled={saving} aria-label="Cerrar" sx={appModalCloseButtonSx}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <Divider sx={appModalHeaderDividerSx} />
      <DialogContent
        ref={scrollRef}
        data-testid="manual-inquiry-scroll"
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          px: '20px',
          pt: 2,
          pb: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: SECTION_GAP,
          '& > *': { flexShrink: 0 },
          ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
        }}
      >
        <Box>
          <Typography id="manual-inquiry-lead-label" sx={{ ...leadsSectionTitleSx, mb: LABEL_GAP }}>Lead</Typography>
          {!presetLead && (
            <Box role="group" aria-labelledby="manual-inquiry-lead-label" sx={leadModeGridSx}>
              {[['existing', 'Lead existente'], ['new', 'Lead nuevo']].map(([mode, label]) => (
                <Button
                  key={mode}
                  size="small"
                  disabled={saving}
                  aria-pressed={form.mode === mode}
                  onClick={() => update({ mode })}
                  sx={{ ...getLeadStageFilterChipSx(form.mode === mode), ...leadsGridChipSx }}
                >
                  {label}
                </Button>
              ))}
            </Box>
          )}
          {isNew ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: FIELD_GAP }}>
              <TextField
                label="Nombre"
                value={form.name}
                onChange={setField('name')}
                required
                fullWidth
                size="small"
                inputProps={{ maxLength: 120 }}
                error={(touched && nameMissing) || Boolean(fieldError('name'))}
                helperText={fieldError('name') || (touched && nameMissing ? 'Cargá el nombre.' : '')}
                sx={leadsTextFieldSx}
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: FIELD_GAP, alignItems: 'start' }}>
                <TextField
                  label="Teléfono"
                  value={form.phone}
                  onChange={(e) => update({ phone: sanitizePhoneInput(e.target.value) })}
                  size="small"
                  fullWidth
                  autoComplete="tel"
                  inputProps={{ maxLength: 40, inputMode: 'tel' }}
                  error={(touched && (contactMissing || phoneInvalid)) || Boolean(fieldError('phone') || fieldError('contact'))}
                  helperText={phoneHelper}
                  sx={leadsTextFieldSx}
                />
                <TextField
                  label="Email"
                  value={form.email}
                  onChange={setField('email')}
                  size="small"
                  fullWidth
                  type="email"
                  autoComplete="email"
                  inputProps={{ maxLength: 254, inputMode: 'email' }}
                  error={(touched && (contactMissing || emailInvalid)) || Boolean(fieldError('email') || fieldError('contact'))}
                  helperText={emailHelper}
                  sx={leadsTextFieldSx}
                />
              </Box>
              {duplicateInSection ? duplicateNotice : null}
            </Box>
          ) : (
            <LeadPicker
              value={form.lead}
              onChange={(lead) => update({ lead })}
              ownerUserId={ownerUserId}
              locked={Boolean(presetLead)}
              disabled={saving}
              error={fieldError('lead') || (touched && leadMissing ? 'Elegí un lead o cargá uno nuevo.' : '')}
            />
          )}
        </Box>
        <LeadChannelPicker
          value={form.channel}
          disabled={saving}
          onChange={(channel) => update({ channel })}
        />
        <TextField
          label="¿Qué consultó?"
          placeholder="Ej.: llamó por la UF 3B, quiere visitar el sábado"
          value={form.message}
          onChange={setField('message')}
          required
          size="small"
          fullWidth
          multiline
          rows={3}
          inputProps={{ maxLength: MANUAL_INQUIRY_MESSAGE_MAX }}
          error={(touched && messageMissing) || Boolean(fieldError('message'))}
          helperText={fieldError('message') || (touched && messageMissing ? 'Escribí qué consultó.' : '')}
          sx={leadsTextFieldSx}
        />
        <TextField
          label="Fecha y hora"
          type="datetime-local"
          value={form.occurredAt}
          onChange={setField('occurredAt')}
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: toDateTimeLocalValue(new Date()), 'aria-label': 'Fecha y hora de la consulta' }}
          error={occurredFuture || Boolean(fieldError('occurredAt'))}
          helperText={fieldError('occurredAt') || (occurredFuture ? 'La fecha no puede ser futura.' : 'Cuándo te consultó. Por defecto, ahora.')}
          sx={leadsDateFieldSx}
        />
        <LeadRecordPicker
          value={form.records}
          onChange={(records) => update({ records })}
          max={MANUAL_INQUIRY_MAX_RECORDS}
          ownerUserId={ownerUserId}
          disabled={saving}
          errorIndex={error?.field === 'record' ? error.recordIndex : null}
          error={fieldError('record')}
          hint="Una consulta por registro. Sin registro, la podés asignar después."
        />
        {generalError?.duplicateId && !duplicateInSection ? duplicateNotice : null}
        {generalError && !generalError.duplicateId ? (
          <Alert ref={feedbackRef} severity="error" variant="outlined" sx={leadsAlertSx}>
            {generalError.message}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={appModalActionsSx}>
        <Button variant="outlined" onClick={onClose} disabled={saving} sx={leadsGhostButtonSx}>Cancelar</Button>
        <Button
          type="submit"
          variant="contained"
          disabled={saving}
          sx={leadsPrimaryButtonSx}
          startIcon={saving ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
        >
          Crear consulta
        </Button>
      </DialogActions>
    </Dialog>
  );
}
